import { fetcher } from "@/helpers/fetcher";
import { SearchQuery } from "@/types/common";
import {
  InstructionCompleteRequest,
  InstructionRejectRequest,
  InstructionSearchCondition,
  InstructionsResponse,
  InstructionStatisticsResponse,
  InstructionStatusChangeRequest,
} from "@/types/instruction";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import qs from "qs";

export interface MonthlyStats {
  month: number;
  registered: number;
  inProgress: number;
  completed: number;
  closed: number;
  delayed: number;
  completionRate: number;
  onTimeRate: number;
}

export const useInstructionSearch = (searchQuery: SearchQuery<InstructionSearchCondition>) => {
  return useQuery<InstructionsResponse>({
    queryKey: ["instructions", qs.stringify(searchQuery)],
    queryFn: async ({ signal }): Promise<InstructionsResponse> =>
      fetcher({ url: "/common/instructions", params: searchQuery, signal }),
    placeholderData: (previous) => previous,
  });
};

export const queryInstructionDetail = (id: number) => ({
  queryKey: ["instruction", id],
  queryFn: () => fetcher({ url: `/common/instructions/${id}` }),
  enabled: !!id,
  staleTime: 0,
  cacheTime: 0,
});

export const useInstructionCreateOrUpdate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formData, files }: { formData: any; files: File[] }) => {
      const fd = new FormData();
      files.forEach((file) => fd.append("attachments", file));
      fd.append("data", new Blob([JSON.stringify(formData)], { type: "application/json" }));

      return fetcher({
        url: "/common/instructions",
        method: "POST",
        headers: { "Content-Type": "multipart/form-data" },
        data: fd,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      if (variables.formData.id) {
        queryClient.invalidateQueries({ queryKey: ["instruction", variables.formData.id] });
      }
      navigate({ to: "/instructions" });
    },
  });
};

// 삭제
export const useInstructionDelete = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => fetcher({ url: `/common/instructions/${id}`, method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      navigate({ to: "/instructions" });
    },
  });
};

// 이행확정
export const useInstructionExecuteRequest = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => fetcher({ url: `/approver/instructions/${id}/confirm-execution`, method: "POST" }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      queryClient.invalidateQueries({ queryKey: ["instruction", id] });
      navigate({ to: "/instructions" });
    },
  });
};

// 반려
export const useInstructionReject = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: InstructionRejectRequest) =>
      fetcher({ url: `/approver/instructions/${request.id}/reject`, method: "POST", data: { reason: request.reason } }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ["instruction", variables.id] });
      }

      navigate({ to: "/instructions" });
    },
  });
};

// 완료요청
export const useInstructionCompleteRequest = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ request, files }: { request: InstructionCompleteRequest; files: File[] }) => {
      const fd = new FormData();
      files.forEach((file) => fd.append("attachments", file));
      fd.append("data", new Blob([JSON.stringify(request)], { type: "application/json" }));

      return fetcher({
        url: `/common/instructions/${request.id}/complete-request`,
        method: "POST",
        headers: { "Content-Type": "multipart/form-data" },
        data: fd,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      if (variables.request.id) {
        queryClient.invalidateQueries({ queryKey: ["instruction", variables.request.id] });
      }
      navigate({ to: "/instructions" });
    },
  });
};

// 완료확정
export const useInstructionComplete = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => fetcher({ url: `/approver/instructions/${id}/confirm`, method: "POST" }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      queryClient.invalidateQueries({ queryKey: ["instruction", id] });
      navigate({ to: "/instructions" });
    },
  });
};

// 상태 변경
export const useInstructionStatusChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: InstructionStatusChangeRequest }) =>
      fetcher({ url: `/approver/instructions/${id}/status`, method: "PATCH", data: request }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instructions"] });
      queryClient.invalidateQueries({ queryKey: ["instruction", variables.id] });
    },
  });
};

// 통계
export const useInstructionStatistics = (year: number) => {
  return useQuery<InstructionStatisticsResponse>({
    queryKey: ["instruction-statistics", year],
    queryFn: async ({ signal }): Promise<InstructionStatisticsResponse> =>
      fetcher({ url: `/common/instructions/statistics/${year}`, signal }),
  });
};
